<template>
  <div class="email-verification">
    <div class="verification-container">
      <div class="verification-card">
        <!-- Estado de carga -->
        <div v-if="isLoading" class="loading-state">
          <div class="spinner"></div>
          <h2>Verificando tu email...</h2>
          <p>Por favor espera mientras verificamos tu dirección de correo electrónico.</p>
        </div>

        <!-- Estado de éxito -->
        <div v-else-if="verificationStatus === 'success'" class="success-state">
          <div class="success-icon">✅</div>
          <h2>¡Email verificado exitosamente!</h2>
          <p>Tu dirección de correo electrónico ha sido verificada correctamente.</p>
          <p>Ya puedes iniciar sesión en tu cuenta.</p>
          <div class="action-buttons">
            <button @click="goToLogin" class="btn-primary">
              Iniciar Sesión
            </button>
            <button @click="goToHome" class="btn-secondary">
              Ir al Inicio
            </button>
          </div>
        </div>

        <!-- Estado de error -->
        <div v-else-if="verificationStatus === 'error'" class="error-state">
          <div class="error-icon">❌</div>
          <h2>Error en la verificación</h2>
          <p class="error-message">{{ errorMessage }}</p>
          <div class="error-details">
            <p>Posibles causas:</p>
            <ul>
              <li>El enlace de verificación ha expirado</li>
              <li>El enlace ya fue utilizado anteriormente</li>
              <li>El enlace es inválido o está corrupto</li>
            </ul>
          </div>
          <div class="action-buttons">
            <button @click="resendVerification" class="btn-primary" :disabled="isResending">
              {{ isResending ? 'Enviando...' : 'Reenviar Verificación' }}
            </button>
            <button @click="goToHome" class="btn-secondary">
              Ir al Inicio
            </button>
          </div>
        </div>

        <!-- Estado de token faltante -->
        <div v-else-if="verificationStatus === 'no-token'" class="error-state">
          <div class="error-icon">⚠️</div>
          <h2>Token de verificación no encontrado</h2>
          <p>No se encontró un token de verificación válido en la URL.</p>
          <p>Por favor, utiliza el enlace completo que recibiste en tu correo electrónico.</p>
          <div class="action-buttons">
            <button @click="goToHome" class="btn-secondary">
              Ir al Inicio
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getApiUrl, config } from '@/config';

export default {
  name: 'AppEmailVerification',
  data() {
    return {
      isLoading: true,
      verificationStatus: null, // 'success', 'error', 'no-token'
      errorMessage: '',
      isResending: false
    }
  },
  async mounted() {
    await this.verifyEmail();
  },
  methods: {
    async verifyEmail() {
      try {
        // Obtener el token de la URL
        const token = this.$route.query.token;
        
        if (!token) {
          this.verificationStatus = 'no-token';
          this.isLoading = false;
          return;
        }

        // Llamada al backend para verificar el email
        const response = await fetch(getApiUrl(config.endpoints.verifyEmail), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token })
        });

        if (response.ok) {
          const data = await response.json();
          this.verificationStatus = 'success';
          console.log('Email verificado exitosamente:', data);
        } else {
          const errorData = await response.json();
          this.verificationStatus = 'error';
          this.errorMessage = errorData.message || 'Error desconocido durante la verificación';
          console.error('Error en verificación:', errorData);
        }
      } catch (error) {
        this.verificationStatus = 'error';
        this.errorMessage = 'Error de conexión. Por favor, intenta más tarde.';
        console.error('Error de red:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async resendVerification() {
      this.isResending = true;
      try {
        // Aquí iría la lógica para reenviar la verificación
        // Por ahora, simulamos la acción
        await new Promise(resolve => setTimeout(resolve, 2000));
        alert('Se ha enviado un nuevo email de verificación. Por favor revisa tu bandeja de entrada.');
      } catch (error) {
        alert('Error al reenviar la verificación. Por favor, intenta más tarde.');
      } finally {
        this.isResending = false;
      }
    },

    goToLogin() {
      this.$router.push('/login');
    },

    goToHome() {
      this.$router.push('/');
    }
  }
}
</script>

<style scoped>
.email-verification {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.verification-container {
  width: 100%;
  max-width: 500px;
}

.verification-card {
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
}

/* Estados de carga */
.loading-state {
  padding: 20px 0;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Estados de éxito y error */
.success-state,
.error-state {
  padding: 20px 0;
}

.success-icon,
.error-icon {
  font-size: 4rem;
  margin-bottom: 20px;
}

.success-state h2 {
  color: #28a745;
  margin-bottom: 16px;
  font-size: 1.8rem;
}

.error-state h2 {
  color: #dc3545;
  margin-bottom: 16px;
  font-size: 1.8rem;
}

.success-state p,
.error-state p {
  color: #666;
  margin-bottom: 12px;
  line-height: 1.5;
}

.error-message {
  color: #dc3545;
  font-weight: 500;
  margin-bottom: 20px;
}

.error-details {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  text-align: left;
}

.error-details p {
  margin-bottom: 10px;
  font-weight: 500;
  color: #495057;
}

.error-details ul {
  margin: 0;
  padding-left: 20px;
}

.error-details li {
  margin-bottom: 8px;
  color: #6c757d;
}

/* Botones */
.action-buttons {
  margin-top: 30px;
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-primary,
.btn-secondary {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 140px;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5a6fd8;
  transform: translateY(-2px);
}

.btn-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
  transform: translateY(-2px);
}

/* Responsive */
@media (max-width: 768px) {
  .verification-card {
    padding: 30px 20px;
  }
  
  .action-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .btn-primary,
  .btn-secondary {
    width: 100%;
    max-width: 250px;
  }
}
</style>
